import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import {
  VaLoadingIndicator,
  VaRadio,
  VaRadioOption,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { withRouter } from 'react-router';

import { apiRequest } from '@department-of-veterans-affairs/platform-utilities/api';
import { focusElement } from 'platform/utilities/ui';
import FormNavButtons from '~/platform/forms-system/src/js/components/FormNavButtons';

import { ServerErrorAlert } from '../config/helpers';
import {
  ERRORED_CATEGORY_PREDICTION,
  URL,
  USER_ADOPTION,
  getApiUrl,
  hasPrefillInformation,
} from '../constants';
import { askVAAttachmentStorage } from '../utils/StorageAdapter';
import { predictCategory } from '../utils/api';

const PREDICTION_TIMEOUT_MS = 5000;
const NONE_VALUE = String(USER_ADOPTION.DECLINED_SUGGESTIONS);

const SuggestedCategorySelectPage = props => {
  const {
    onChange,
    formData,
    goBack,
    goForward,
    contentBeforeButtons,
    contentAfterButtons,
  } = props;
  const isLOA3 = useSelector(state => state.user.profile.loa.current === 3);

  const [predictions, setPredictions] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, isLoading] = useState(true);
  const [categoriesLoading, isCategoriesLoading] = useState(true);
  const [error, hasError] = useState(false);
  const [selectedValue, setSelectedValue] = useState(null);
  const [validationError, setValidationError] = useState(null);

  // Guards the mount-only effect below so listing its real deps doesn't cause repeat runs.
  const hasFetchedPrediction = useRef(false);
  // onChange/goForward still work after unmount, so a late prediction result must be ignored
  const isMounted = useRef(true);

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );

  useEffect(() => {
    if (hasFetchedPrediction.current) return;
    hasFetchedPrediction.current = true;

    const stored = formData.predictions?.category;
    if (stored?.predictions) {
      setPredictions(stored.predictions);
      const previousValue = String(stored.userAdoption);
      if (previousValue === NONE_VALUE || stored.predictions[previousValue]) {
        setSelectedValue(previousValue);
      }
      isLoading(false);
      return;
    }

    const timeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('timeout')), PREDICTION_TIMEOUT_MS),
    );

    Promise.race([predictCategory(formData.question), timeout])
      .then(res => {
        if (!isMounted.current) return;
        setPredictions(res.predictions);
        onChange({
          ...formData,
          predictions: {
            ...formData.predictions,
            category: {
              userAdoption: USER_ADOPTION.ERROR,
              modelName: res.modelName,
              modelVersion: res.modelVersion,
              predictions: res.predictions,
            },
          },
        });
        isLoading(false);
      })
      .catch(() => {
        if (!isMounted.current) return;
        // Forms saved before updateFormData defaulted category may lack it; selectCategory depends on ERROR
        const erroredFormData = {
          ...formData,
          predictions: {
            ...formData.predictions,
            category: ERRORED_CATEGORY_PREDICTION,
          },
        };
        onChange(erroredFormData);
        goForward(erroredFormData);
      });
  }, [formData, onChange, goForward]);

  useEffect(() => {
    apiRequest(getApiUrl(URL.GET_CATEGORIES))
      .then(res => {
        setCategories(res.data);
        isCategoriesLoading(false);
      })
      .catch(() => {
        isCategoriesLoading(false);
        hasError(true);
      });
  }, []);

  useEffect(() => {
    focusElement('h2');
  }, [loading]);

  const commitSelection = value => {
    if (value === NONE_VALUE) {
      onChange({
        ...formData,
        categoryId: null,
        selectCategory: '',
        categoryAllowAttachments: false,
        categoryRequiresSignIn: false,
        requireSignInLogic: {
          ...formData.requireSignInLogic,
          category: false,
        },
        contactPreferences: [],
        predictions: {
          ...formData.predictions,
          category: {
            ...formData.predictions?.category,
            userAdoption: USER_ADOPTION.DECLINED_SUGGESTIONS,
          },
        },
      });
      return;
    }

    const categoryId = predictions[value].category.id;
    const selected = categories.find(cat => cat.id === categoryId);

    if (!selected) {
      hasError(true);
      return;
    }

    // fire-and-forget so selecting a tile doesn't block on storage cleanup
    (async () => {
      await askVAAttachmentStorage.clear();
    })();

    const initialData =
      formData.initialFormData === undefined
        ? { ...formData }
        : { ...formData.initialFormData };

    onChange({
      ...formData,
      hasPrefillInformation: hasPrefillInformation(formData),
      initialFormData: initialData,
      categoryId: selected.id,
      selectCategory: selected.attributes.name,
      categoryAllowAttachments: selected.attributes.allowAttachments,
      contactPreferences: selected.attributes.contactPreferences,
      categoryRequiresSignIn:
        selected.attributes.requiresAuthentication && !isLOA3,
      requireSignInLogic: formData.requireSignInLogic
        ? {
            ...formData.requireSignInLogic,
            category: selected.attributes.requiresAuthentication,
          }
        : {
            category: selected.attributes.requiresAuthentication,
            topic: false,
            personQuestionIsAbout: false,
          },
      predictions: {
        ...formData.predictions,
        category: {
          ...formData.predictions?.category,
          userAdoption: Number(value),
        },
      },
    });
  };

  const handleChange = event => {
    const { value } = event.detail;
    setSelectedValue(value);
    setValidationError(null);
    commitSelection(value);
  };

  const handleContinue = () => {
    if (!selectedValue) {
      setValidationError('Select a suggested category');
      focusElement('va-radio');
      return;
    }

    goForward(formData);
  };

  const handleGoBack = () => {
    goBack();
  };

  if (error) return <ServerErrorAlert />;

  const sortedPredictionKeys = Object.keys(predictions || {}).sort();
  // The top prediction is always keyed "1"
  const additionalPredictionKeys = sortedPredictionKeys.filter(
    key => key !== '1',
  );
  const headingClasses =
    'vads-u-font-size--md vads-u-font-weight--bold vads-u-color--base vads-u-margin-bottom--1';

  const renderPredictionOption = key => (
    <VaRadioOption
      key={key}
      tile
      name="root_suggestedCategory"
      id={`suggested-category-${key}`}
      value={key}
      label={predictions[key].category.name}
      description={predictions[key].category.description}
      checked={selectedValue === key}
      uswds
    />
  );

  return (
    <>
      <dl className="vads-u-background-color--gray-light-alt vads-u-width--full vads-u-padding--2p5 vads-u-margin-y--3">
        <dt className="vads-u-font-size--md vads-u-font-weight--bold vads-u-margin-bottom--1p5">
          What do you need help with?
        </dt>
        <dd>{formData.question}</dd>
      </dl>
      <form className="rjsf" data-dd-privacy="allow">
        <div
          className={
            validationError
              ? 'vads-u-border-left--4px vads-u-border-color--secondary-dark vads-u-padding-left--2'
              : ''
          }
        >
          <h3
            id="suggested-categories-heading"
            className="vads-u-margin-top--3 vads-u-margin-bottom--1 vads-u-display--flex vads-u-align-items--flex-start"
          >
            <va-icon
              icon="auto_awesome"
              size={3}
              class="vads-u-margin-right--1"
              aria-hidden="true"
            />
            <span>
              Suggested categories
              <span className="vads-u-color--secondary-dark vads-u-font-family--sans vads-u-font-size--base vads-u-font-weight--normal vads-u-margin-left--1">
                (*Required)
              </span>
            </span>
          </h3>
          {validationError && (
            <span
              id="suggested-category-error"
              role="alert"
              className="vads-u-display--block vads-u-color--secondary-dark vads-u-font-weight--bold vads-u-margin-bottom--1"
            >
              <span className="usa-sr-only">Error</span> {validationError}
            </span>
          )}
          <p id="suggested-categories-description">
            We use AI tools to help with these suggestions. They’re based on
            your question and similar questions from other Veterans.
          </p>
          {loading || categoriesLoading ? (
            <VaLoadingIndicator
              className="vads-u-margin-top--2 vads-u-margin-bottom--4"
              message="Loading suggested categories..."
            />
          ) : (
            <div
              role="group"
              aria-labelledby="suggested-categories-heading"
              aria-describedby={`${
                validationError ? 'suggested-category-error ' : ''
              }suggested-categories-description`}
            >
              <VaRadio
                className="vads-u-margin-top--neg2 vads-u-margin-bottom--2"
                onVaValueChange={handleChange}
                label=""
                name="root_suggestedCategory"
                uswds
              >
                {sortedPredictionKeys.includes('1') && (
                  <>
                    <h4 className={`${headingClasses} vads-u-margin-top--0`}>
                      Top suggestion
                    </h4>
                    {renderPredictionOption('1')}
                  </>
                )}
                {additionalPredictionKeys.length > 0 && (
                  <>
                    <h4 className={`${headingClasses} vads-u-margin-top--2`}>
                      Additional suggestions
                    </h4>
                    {additionalPredictionKeys.map(renderPredictionOption)}
                  </>
                )}
                <VaRadioOption
                  tile
                  name="root_suggestedCategory"
                  id="suggested-category-none"
                  value={NONE_VALUE}
                  label="None of these categories"
                  description="Instead of using an AI-suggested category, you can pick a category yourself"
                  checked={selectedValue === NONE_VALUE}
                  uswds
                />
              </VaRadio>
            </div>
          )}
        </div>
        {contentBeforeButtons}
        <FormNavButtons goBack={handleGoBack} goForward={handleContinue} />
        {contentAfterButtons}
      </form>
    </>
  );
};

SuggestedCategorySelectPage.propTypes = {
  contentAfterButtons: PropTypes.node,
  contentBeforeButtons: PropTypes.node,
  formData: PropTypes.object,
  formId: PropTypes.string,
  goBack: PropTypes.func,
  goForward: PropTypes.func,
  goToPath: PropTypes.func,
  id: PropTypes.string,
  router: PropTypes.object,
  onChange: PropTypes.func,
};

function mapStateToProps(state) {
  return {
    formData: state.form.data,
    formId: state.form.formId,
  };
}

export default connect(mapStateToProps)(
  withRouter(SuggestedCategorySelectPage),
);
